import { loanRequestsApi } from 'app/services/api';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { formatDate } from 'utils/date';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const MS_PER_MINUTE = SECONDS_PER_MINUTE * MS_PER_SECOND;
const MS_PER_HOUR = MINUTES_PER_HOUR * MS_PER_MINUTE;

// LoanRequests timestamps are stamped in UTC; includeLocalOffset renders them in the admin's own
// time zone, matching the Entity Audit History and Notification Dispatch History logs.
export const formatRequestDate = value =>
  _.isNil(value) ? null : formatDate(value, COMMON_DATETIME_FORMAT, { includeLocalOffset: true });

export const formatDuration = (start, end) => {
  if (_.isNil(start) || _.isNil(end)) {
    return null;
  }

  const startDate = dayjs(start);
  const endDate = dayjs(end);

  if (!startDate.isValid() || !endDate.isValid()) {
    return null;
  }

  const ms = endDate.diff(startDate);

  if (ms < 0) {
    return null;
  }

  if (ms < MS_PER_SECOND) {
    return `${ms}ms`;
  }

  if (ms < MS_PER_MINUTE) {
    return `${(ms / MS_PER_SECOND).toFixed(1)}s`;
  }

  if (ms < MS_PER_HOUR) {
    const totalSeconds = Math.round(ms / MS_PER_SECOND);

    return `${Math.floor(totalSeconds / SECONDS_PER_MINUTE)}m ${
      totalSeconds % SECONDS_PER_MINUTE
    }s`;
  }

  const totalMinutes = Math.round(ms / MS_PER_MINUTE);

  return `${Math.floor(totalMinutes / MINUTES_PER_HOUR)}h ${totalMinutes % MINUTES_PER_HOUR}m`;
};

export const fetchRequestContent = async requestId => {
  const { data } = await loanRequestsApi.getRequestContent(requestId);

  return data;
};

export const fetchResponseContent = async requestId => {
  const { data } = await loanRequestsApi.getResponseContent(requestId);

  return data;
};

export const formatMetadata = metadata => {
  if (_.isNil(metadata)) {
    return null;
  }

  if (_.isString(metadata)) {
    if (metadata.trim().length === 0) {
      return null;
    }

    try {
      return JSON.stringify(JSON.parse(metadata), null, 2);
    } catch {
      return metadata;
    }
  }

  if (_.isEmpty(metadata)) {
    return null;
  }

  return JSON.stringify(metadata, null, 2);
};

export const getDrawerTitle = request => {
  const requestType = _.get(request, 'requestType', '');
  const vendorName = _.get(request, 'vendorName', '');

  return [requestType, vendorName].filter(Boolean).join(' — ');
};

export const formatItemCount = count => `${count} ${count === 1 ? 'request' : 'requests'}`;

export const formatMatchingItemCount = (matchCount, count) =>
  `${matchCount} of ${formatItemCount(count)} match`;

const toSafeSegment = str => str.toLowerCase().replace(/\W+/g, '_');

const DEFAULT_PAYLOAD_EXTENSION = 'txt';

const getPayloadExtension = path => {
  const match = /\.([a-z0-9]+)$/i.exec(_.defaultTo(path, ''));

  return match ? match[1].toLowerCase() : DEFAULT_PAYLOAD_EXTENSION;
};

export const getPayloadDownloadName = (request, direction) => {
  const requestType = toSafeSegment(_.get(request, 'requestType', 'unknown'));
  const vendorName = toSafeSegment(_.get(request, 'vendorName', 'unknown'));
  const extension = getPayloadExtension(_.get(request, [`${direction}Content`, 'path']));

  return `${requestType}_${vendorName}_${direction}.${extension}`;
};
